/**
 * The customer-app API client: one @dp/api client instance wired to runtime
 * config, credentialed CORS, and cookie-based silent refresh.
 *
 * Auth model: access token in memory, refresh token in the
 * HttpOnly `__Host-dp_refresh` cookie. `refresh()` posts to /auth/refresh with
 * `credentials: "include"` and NO body (skipAuth so no stale bearer attaches),
 * returning the new short-lived access token — or null when the session is gone.
 */
import { createClient, type ApiClient } from "@dp/api";
import type { AppConfig } from "../config.js";

let client: ApiClient | null = null;

/** Called once auth fails irrecoverably (refresh rejected) — set by AuthProvider. */
let authFailureHandler: (() => void) | null = null;
export function setAuthFailureHandler(fn: () => void): void {
  authFailureHandler = fn;
}

export function initClient(config: AppConfig): ApiClient {
  const base = config.apiBaseUrl.replace(/\/$/, "");

  const c = createClient({
    baseUrl: base,
    credentials: "include",
    onAuthFailure: () => authFailureHandler?.(),
    refresh: async () => {
      // Cookie-based refresh: no body, credentials included, no bearer.
      try {
        const res = await fetch(`${base}/auth/refresh`, {
          method: "POST",
          credentials: "include",
        });
        if (!res.ok) return null;
        const body = (await res.json()) as { access_token?: string };
        return body.access_token ?? null;
      } catch {
        return null;
      }
    },
  });

  client = c;
  return c;
}

export function getClient(): ApiClient {
  if (!client) throw new Error("API client not initialized — call initClient(config) at boot");
  return client;
}
